const DEFAULT_API_BASE_URL = "https://api.dhaba.ai";
/**
 * Dev / test default: the local API. A dev server, `vite build --mode
 * development` or the mocked Playwright suite must never reach production
 * (HTTP or websocket) just because no VITE_* URL was set.
 */
const LOCAL_API_BASE_URL = "http://127.0.0.1:8000";
/** The Dhaba.ai Thermal Print Service (TPS 2.x) listens on loopback only, plain HTTP. */
export const DEFAULT_TPS_BASE_URL = "http://127.0.0.1:38445";

export interface AppConfig {
  apiBaseUrl: string;
  wsBaseUrl: string;
  tpsBaseUrl: string;
  googleSignInClientId: string | null;
  errorReportingEnabled: boolean;
  errorReportEndpoint: string | null;
  mode: string;
  isDevelopment: boolean;
  isProduction: boolean;
}

type RuntimeEnv = Partial<ImportMetaEnv>;

export function normalizeBaseUrl(value: string | undefined, fallback: string): string {
  const candidate = (value ?? fallback).trim();
  return candidate.replace(/\/+$/, "");
}

export function parseBoolean(value: string | boolean | undefined, fallback = false): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value !== "string") return fallback;

  const normalized = value.trim().toLowerCase();
  if (["1", "true", "yes", "on"].includes(normalized)) return true;
  if (["0", "false", "no", "off"].includes(normalized)) return false;
  return fallback;
}

export function buildAppConfig(env: RuntimeEnv = import.meta.env): AppConfig {
  const isProduction = parseBoolean(env.PROD, false);
  // Only a production build falls back to the production API; the websocket
  // follows whichever API base is in use.
  const apiBaseUrl = normalizeBaseUrl(
    env.VITE_API_BASE_URL,
    isProduction ? DEFAULT_API_BASE_URL : LOCAL_API_BASE_URL,
  );
  const wsFallback = apiBaseUrl.replace(/^http/i, "ws");
  const errorReportEndpoint = env.VITE_ERROR_REPORT_ENDPOINT
    ? normalizeBaseUrl(env.VITE_ERROR_REPORT_ENDPOINT, env.VITE_ERROR_REPORT_ENDPOINT)
    : null;

  return {
    apiBaseUrl,
    wsBaseUrl: normalizeBaseUrl(env.VITE_WS_BASE_URL, wsFallback),
    tpsBaseUrl: normalizeBaseUrl(env.VITE_TPS_BASE_URL, DEFAULT_TPS_BASE_URL),
    googleSignInClientId: env.VITE_GOOGLE_SIGNIN_CLIENT_ID?.trim() || null,
    errorReportingEnabled: parseBoolean(env.VITE_ENABLE_ERROR_REPORTING, false),
    errorReportEndpoint,
    mode: env.MODE ?? "development",
    isDevelopment: parseBoolean(env.DEV, true),
    isProduction,
  };
}

export const appConfig = buildAppConfig();
